import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { HelpCircle, Info } from 'lucide-react';
import { usePanel } from '../contexts/PanelContext';
import type { Field, FieldGroup, AnswerImpact, DocumentPriority } from '../lib/types';
import { generateMdx } from '../lib/markdown';
import { InfoPopover } from '../lib/files';
import { Pill, priorityStyles } from './DocTypeBlock';
import { relevantOptions, visibleGroupFields } from '../lib/relevance';

// A stable default for pulseFieldIds, so fields don't re-render each time.
const EMPTY_PULSE_SET: Set<string> = new Set();

// The page builds this across every section, since a gate can reference a field in another section.
export function buildFieldsLookup(groups: FieldGroup[]): Record<string, Field | undefined> {
  const lookup: Record<string, Field | undefined> = {};
  for (const g of groups) {
    for (const f of (g.fields ?? [])) lookup[f.id] = f;
  }
  return lookup;
}

function formatConditionValue(field: Field | undefined, value: any): string {
  if (value === '*') return 'any value';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (Array.isArray(value)) {
    return value.map(v => formatConditionValue(field, v)).join(', ');
  }
  if (field?.options) {
    const opt = field.options.find(o => o.value === value);
    if (opt) return opt.label;
  }
  return String(value);
}

function RelevanceBadge({
  relevant_when, fieldsLookup,
}: {
  relevant_when: Record<string, any> | undefined;
  fieldsLookup: Record<string, Field | undefined>;
}) {
  if (!relevant_when) return null;
  const reasons: string[] = [];
  for (const [fieldId, expected] of Object.entries(relevant_when)) {
    const field = fieldsLookup[fieldId];
    const label = field?.label || fieldId;
    const displayValue = formatConditionValue(field, expected);
    reasons.push(`"${displayValue}" to "${label}"`);
  }
  if (reasons.length === 0) return null;
  return (
    <div className="flex items-start gap-2 text-xs text-insight bg-insight/5 border border-insight/20 rounded-lg px-3 py-2 mb-4">
      <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
      <span>Shown because you answered {reasons.join(' and ')}</span>
    </div>
  );
}

// The "optional" group catches any unrecognised priority.
const IMPACT_PRIORITY_GROUPS: { key: DocumentPriority; match: (p: string) => boolean }[] = [
  { key: 'required', match: p => p === 'required' },
  { key: 'recommended', match: p => p === 'recommended' },
  { key: 'optional', match: p => p !== 'required' && p !== 'recommended' },
];

export function QuestionImpactInfo({ impact }: { impact: AnswerImpact[] }) {
  return (
    <InfoPopover ariaLabel="How each answer affects your documents">
      <div className="w-72 max-h-72 overflow-y-auto text-xs">
        <p className="text-[11px] text-coolslate mb-2">
          Here are the documents that will be added based on your answer to this question.
        </p>
        <div className="space-y-2.5">
          {impact.map(a => (
            <div key={String(a.value)} className="border-t border-softgrey pt-2 first:border-t-0 first:pt-0">
              <div className="font-medium text-graphite mb-1">{a.label}</div>
              {IMPACT_PRIORITY_GROUPS.map(g => {
                const docs = a.includes.filter(d => g.match(d.priority));
                if (docs.length === 0) return null;
                return (
                  <div key={g.key} className="mb-1.5 last:mb-0 pl-2">
                    <div className="mb-1 pt-1">
                      <Pill style={priorityStyles[g.key]} />
                    </div>
                    <ul className="space-y-0.5 pl-2">
                      {docs.map(doc => (
                        <li key={doc.id}>
                          <Link to={`/types/${doc.id}`} className="block truncate text-coolslate hover:text-brand-dark hover:underline">
                            {doc.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </InfoPopover>
  );
}

function FieldLabel({ label }: { label: string }) {
  return (
    <label className="block mb-1.5">
      <span className="text-sm font-medium text-graphite">{label}</span>
    </label>
  );
}

export function FieldInput({ field, value, onChange }: {
  field: Field;
  value: any;
  onChange: (value: any) => void;
}) {
  const inputType = field.input_type;

  if (inputType === 'yes_no') {
    return (
      <div>
        <FieldLabel label={field.label} />
        {field.help_text && <p className="text-[13px] text-coolslate mb-2.5">{field.help_text}</p>}
        <div className="flex gap-3">
          <button
            onClick={() => onChange(true)}
            className={`flex-1 py-3 px-4 text-sm font-medium rounded-lg transition-colors cursor-pointer ${value === true
              ? 'bg-graphite text-white'
              : 'bg-white text-graphite border border-softgrey hover:border-brand/30'
              }`}
          >
            Yes
          </button>
          <button
            onClick={() => onChange(false)}
            className={`flex-1 py-3 px-4 text-sm font-medium rounded-lg transition-colors cursor-pointer ${value === false
              ? 'bg-graphite text-white'
              : 'bg-white text-graphite border border-softgrey hover:border-brand/30'
              }`}
          >
            No
          </button>
        </div>
      </div>
    );
  }

  if (inputType === 'checkbox') {
    return (
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-softgrey text-brand focus:ring-brand cursor-pointer"
        />
        <div>
          <span className="text-sm text-graphite group-hover:text-brand-dark transition-colors">{field.label}</span>
          {field.help_text && <p className="text-[13px] text-coolslate mt-0.5">{field.help_text}</p>}
        </div>
      </label>
    );
  }

  if (inputType === 'single_choice') {
    return (
      <div>
        <FieldLabel label={field.label} />
        {field.help_text && <p className="text-[13px] text-coolslate mb-2">{field.help_text}</p>}
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full text-sm px-4 py-3 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand cursor-pointer"
        >
          <option value="">Select...</option>
          {field.options?.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  }

  if (inputType === 'date') {
    return (
      <div>
        <FieldLabel label={field.label} />
        {field.help_text && <p className="text-[13px] text-coolslate mb-2">{field.help_text}</p>}
        <input
          type="date"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full text-sm px-4 py-3 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand"
        />
      </div>
    );
  }

  if (inputType === 'currency') {
    return (
      <div>
        <FieldLabel label={field.label} />
        {field.help_text && <p className="text-[13px] text-coolslate mb-2">{field.help_text}</p>}
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-coolslate">{field.currency === undefined ? '$' : field.currency}</span>
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            placeholder="0.00"
            className={`w-full text-sm ${field.currency ? 'pl-14' : field.currency === '' ? 'pl-4' : 'pl-8'} pr-4 py-3 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand`}
          />
        </div>
      </div>
    );
  }

  if (inputType === 'number') {
    return (
      <div>
        <FieldLabel label={field.label} />
        {field.help_text && <p className="text-[13px] text-coolslate mb-2">{field.help_text}</p>}
        <input
          type="number"
          step="any"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="w-full text-sm px-4 py-3 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand"
        />
      </div>
    );
  }

  if (inputType === 'integer') {
    return (
      <div>
        <FieldLabel label={field.label} />
        {field.help_text && <p className="text-[13px] text-coolslate mb-2">{field.help_text}</p>}
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          placeholder="0"
          className="w-full text-sm px-4 py-3 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand"
        />
      </div>
    );
  }

  if (inputType === 'multi_choice') {
    const selected: string[] = Array.isArray(value) ? value : [];
    const toggle = (optValue: string) => {
      const next = selected.includes(optValue)
        ? selected.filter(v => v !== optValue)
        : [...selected, optValue];
      onChange(next.length > 0 ? next : null);
    };
    return (
      <div>
        <FieldLabel label={field.label} />
        {field.help_text && <p className="text-[13px] text-coolslate mb-2.5">{field.help_text}</p>}
        <div className="grid grid-cols-2 gap-2.5">
          {field.options?.map(opt => {
            const isSelected = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className={`flex items-center gap-2.5 px-4 py-3 text-sm rounded-lg border transition-colors cursor-pointer text-left ${isSelected
                  ? 'border-brand bg-brand/5 text-brand-dark font-medium'
                  : 'border-softgrey bg-white text-graphite hover:border-brand/30'
                  }`}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-brand border-brand' : 'border-softgrey'}`}>
                  {isSelected && (
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-graphite mb-1.5">{field.label}</label>
      {field.help_text && <p className="text-[13px] text-coolslate mb-2">{field.help_text}</p>}
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={`Enter ${field.label.toLowerCase()}...`}
        className="w-full text-sm px-4 py-3 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand"
      />
    </div>
  );
}

function FieldGroupCard({ group, fieldValues, onFieldChange, fieldsLookup, highlightFieldId, pulseFieldIds }: {
  group: FieldGroup;
  fieldValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
  fieldsLookup: Record<string, Field | undefined>;
  highlightFieldId: string | null;
  pulseFieldIds: Set<string>;
}) {
  const { openPanel } = usePanel();
  // Relevance is evaluated live against the current answers, so dependent fields show/hide with no re-fetch.
  const visibleFields = visibleGroupFields(group, fieldValues);
  if (visibleFields.length === 0) return null;

  return (
    <div className="border border-softgrey rounded-xl bg-white p-6 mb-5">
      {group.title && (
        <h3 className="text-base font-semibold text-graphite mb-2">{group.title}</h3>
      )}
      {group.intro && (
        <div className="text-sm text-coolslate leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: generateMdx(group.intro) }} />
      )}
      {group.help_link && (
        <button
          onClick={() => openPanel(
            group.help_link!.label,
            <div className="text-sm text-coolslate leading-relaxed" dangerouslySetInnerHTML={{ __html: generateMdx(group.help_link!.help_content) }} />
          )}
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-dark transition-colors cursor-pointer"
        >
          <HelpCircle className="w-4 h-4" />
          {group.help_link.label}
        </button>
      )}
      <RelevanceBadge relevant_when={group.relevant_when} fieldsLookup={fieldsLookup} />
      <div className="space-y-6">
        {visibleFields.map(field => (
          <FieldAnchor
            key={field.id}
            fieldId={field.id}
            highlighted={highlightFieldId === field.id}
            pulse={pulseFieldIds.has(field.id)}
          >
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <FieldInput
                  field={field.options ? { ...field, options: relevantOptions(field, fieldValues) } : field}
                  value={fieldValues[field.id]}
                  onChange={(v) => onFieldChange(field.id, v)}
                />
              </div>
              {field.document_impact && field.document_impact.length > 0 && (
                <div className="pt-0.5">
                  <QuestionImpactInfo impact={field.document_impact} />
                </div>
              )}
            </div>
          </FieldAnchor>
        ))}
      </div>
    </div>
  );
}

function FieldAnchor({ fieldId, highlighted, pulse = false, children }: {
  fieldId: string;
  highlighted: boolean;
  pulse?: boolean;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (!highlighted || !ref.current) return;
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 2200);
    return () => clearTimeout(t);
  }, [highlighted]);

  const ring = flash || pulse;
  return (
    <div
      ref={ref}
      id={`field-${fieldId}`}
      className={`scroll-mt-24 rounded-lg transition-all ${ring ? 'ring-2 ring-brand/60 ring-offset-2 ring-offset-white' : ''}`}
    >
      {children}
    </div>
  );
}

export function SectionQuestionsTab({
  fieldGroups, fieldValues, onFieldChange, highlightFieldId = null, pulseFieldIds = EMPTY_PULSE_SET,
  fieldsLookup,
}: {
  fieldGroups: FieldGroup[];
  fieldValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
  highlightFieldId?: string | null;
  pulseFieldIds?: Set<string>;
  fieldsLookup?: Record<string, Field | undefined>;
}) {
  const [, setSearchParams] = useSearchParams();

  // Once the highlighted question gets an answer, drop the `field` param so reloads don't keep flashing it.
  const handleFieldChange = (fieldId: string, value: any) => {
    onFieldChange(fieldId, value);
    if (fieldId === highlightFieldId) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('field');
        return next;
      }, { replace: true });
    }
  };

  const lookup = fieldsLookup ?? buildFieldsLookup(fieldGroups);

  if (fieldGroups.length === 0) {
    return (
      <div className="text-center py-12 text-coolslate">
        No questions in this section.
      </div>
    );
  }

  return (
    <div>
      {fieldGroups.map(group => (
        <FieldGroupCard
          key={group.id}
          group={group}
          fieldValues={fieldValues}
          onFieldChange={handleFieldChange}
          fieldsLookup={lookup}
          highlightFieldId={highlightFieldId}
          pulseFieldIds={pulseFieldIds}
        />
      ))}
    </div>
  );
}
